import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, X, ArrowLeftRight, Brain, Trash2 } from 'lucide-react';
import type { TradingAnalysis } from '../../types/trading';
import SignalCard from './SignalCard';
import { useDeleteSignal } from '../../hooks/useTradingAnalysis';

interface LiveSignalFeedProps {
  analyses: TradingAnalysis[];
}

const signalIcons = {
  BUY: TrendingUp,
  SELL: TrendingDown,
  HOLD: Minus,
};

const signalColors = {
  BUY: 'text-emerald-400',
  SELL: 'text-red-400',
  HOLD: 'text-amber-400',
};

function formatSignalAge(timestamp: string): string {
  let normalized = (timestamp || '').replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3');
  if (!/[Z+-]\d{0,4}$/.test(normalized)) normalized += 'Z';
  const parsed = new Date(normalized);
  if (isNaN(parsed.getTime())) return '—';

  const diffMs = Date.now() - parsed.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const LiveSignalFeed: React.FC<LiveSignalFeedProps> = ({ analyses }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { mutate: deleteSignal } = useDeleteSignal();

  // Always derive selected from fresh analyses data so feedback updates are reflected
  const selected = selectedId ? analyses.find((a) => a.id === selectedId) ?? null : null;

  if (analyses.length === 0) {
    return (
      <div className="text-center py-4 text-slate-500 text-sm">
        No signals yet. Waiting for EA to send analysis requests...
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {analyses.map((a) => {
          const Icon = signalIcons[a.signal] || Minus;
          const color = signalColors[a.signal] || 'text-slate-400';
          const isConfirmation = (a as any)._source === 'confirmation';

          return (
            <div
              key={a.id}
              onClick={() => setSelectedId(a.id)}
              className="group flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors cursor-pointer"
            >
              <Icon size={16} className={color} />
              <span className="font-medium text-sm text-white">{a.symbol}</span>
              <span className={`text-sm font-semibold ${color}`}>{a.signal}</span>
              <span className="text-xs text-slate-400">{a.confidence}%</span>
              {isConfirmation && (
                <>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    (a as any)._confirmed
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}>
                    {(a as any)._confirmed ? 'Confirmed' : 'Rejected'}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400">
                    {(a as any)._ea_name}
                    {(a as any)._signal_source
                      ? ` · ${(a as any)._signal_source}`
                      : ''}
                  </span>
                </>
              )}
              {(a as any)._confluence_sources?.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">
                  {((a as any)._confluence_sources as string[]).join(' + ')}
                </span>
              )}
              {(a as any)._contra_signal && (a as any)._contra_confidence != null && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${
                  (a as any)._contra_confidence > a.confidence
                    ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                    : 'bg-slate-700/60 text-slate-400'
                }`}>
                  <ArrowLeftRight size={9} />
                  {(a as any)._contra_signal} {(a as any)._contra_confidence}%
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-purple-500/20 text-purple-400 border-purple-500/30 font-medium">
                <Brain size={9} />
                AI
              </span>
              {a.cached && (
                <span className="text-xs text-slate-500 bg-slate-700 px-1.5 py-0.5 rounded">
                  cached
                </span>
              )}
              <span className="ml-auto text-xs text-slate-500 shrink-0">{formatSignalAge(a.timestamp)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSignal({ id: a.id, isConfirmation });
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all shrink-0"
                title="Delete signal"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Analysis detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="relative w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedId(null)}
              className="absolute top-3 right-3 z-10 p-1 rounded-lg bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
            <SignalCard analysis={selected} />
          </div>
        </div>
      )}
    </>
  );
};

export default LiveSignalFeed;
